import { useState, useRef, useCallback, useEffect } from 'react';

interface UseVoiceRecordingOptions {
  onRecordingComplete?: (blob: Blob, durationMs: number) => void;
}

export const useVoiceRecording = (options: UseVoiceRecordingOptions = {}) => {
  const { onRecordingComplete } = options;
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const startTsRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const durationMs = Date.now() - startTsRef.current;
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        cleanup();
        setIsRecording(false);
        setRecordingTime(0);
        onRecordingComplete?.(blob, durationMs);
      };
      mediaRecorderRef.current = recorder;
      startTsRef.current = Date.now();
      setRecordingTime(0);
      setIsRecording(true);
      recorder.start(200);
      timerRef.current = window.setInterval(() => {
        setRecordingTime(Date.now() - startTsRef.current);
      }, 100);
    } catch (err: any) {
      setError(err?.message || 'Microphone access denied');
      cleanup();
      setIsRecording(false);
    }
  }, [cleanup, onRecordingComplete]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    } else {
      cleanup();
      setIsRecording(false);
      setRecordingTime(0);
    }
  }, [cleanup]);

  return {
    isRecording,
    recordingTime,
    error,
    startRecording,
    stopRecording,
  };
};
